// Home — items browser. Adapted from Inku's actual production dashboard
// at /dashboard. Filter chips (All / Canvases / Projects / Briefings)
// + grid of cards (each with a type badge, thumbnail or doc placeholder,
// title, modified time). Top-right: Owner / Sort / Create dropdown.

import {
  ChevronDown,
  FileText,
  Folder,
  Layers,
  Plus,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useStore } from "@/lib/store";
import type { BriefingSummary, Thread } from "@/lib/types";
import { cn } from "@/lib/utils";

type ItemType = "Canvas" | "Project" | "Briefing";

interface Item {
  id: string;
  type: ItemType;
  title: string;
  modifiedAt: number;
  thumbGradient?: string;
  href: string;
}

const TAB_ORDER: Array<{ key: "all" | ItemType; label: string }> = [
  { key: "all", label: "All" },
  { key: "Canvas", label: "Canvases" },
  { key: "Project", label: "Projects" },
  { key: "Briefing", label: "Briefings" },
];

// Deterministic gradient picker so each item gets a stable thumbnail.
const GRADIENTS = [
  "linear-gradient(135deg, #3502F6 0%, #B5A4FF 60%, #FFD08A 100%)",
  "linear-gradient(135deg, #2E7D32 0%, #76C893 60%, #F2EBC1 100%)",
  "linear-gradient(135deg, #6E2A14 0%, #C7935E 60%, #F3EADB 100%)",
  "linear-gradient(135deg, #FFB17A 0%, #FF8FA0 50%, #5A2BFF 100%)",
  "linear-gradient(135deg, #BBE9FF 0%, #C4B6FF 50%, #FFC8DD 100%)",
  "linear-gradient(135deg, #1A007F 0%, #3502F6 60%, #6B4FFF 100%)",
];
const gradientFor = (id: string) =>
  GRADIENTS[
    Math.abs(
      [...id].reduce((a, c) => a + c.charCodeAt(0), 0)
    ) % GRADIENTS.length
  ];

function relTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function threadToItem(t: Thread): Item {
  return {
    id: t.id,
    type: "Canvas",
    title: t.title,
    modifiedAt: t.updatedAt,
    thumbGradient: gradientFor(t.id),
    href: `/t/${t.id}`,
  };
}

function briefingToItem(b: BriefingSummary): Item {
  return {
    id: b.id,
    type: "Briefing",
    title: b.title || "Untitled briefing",
    modifiedAt: new Date(b.updated_at).getTime(),
    href: `/briefings/${b.id}`,
  };
}

export function HomePage() {
  const { threads, briefings, navigate, createBriefing, createThread, me } =
    useStore();

  const [tab, setTab] = useState<"all" | ItemType>("all");

  const items: Item[] = useMemo(() => {
    const all: Item[] = [
      ...threads.map(threadToItem),
      ...briefings.map(briefingToItem),
    ];
    return all.sort((a, b) => b.modifiedAt - a.modifiedAt);
  }, [threads, briefings]);

  const counts = useMemo(
    () => ({
      all: items.length,
      Canvas: items.filter((i) => i.type === "Canvas").length,
      Project: 0, // Demo doesn't have a Projects concept; matches Inku 0/0 state.
      Briefing: items.filter((i) => i.type === "Briefing").length,
    }),
    [items]
  );

  const filtered = useMemo(
    () => (tab === "all" ? items : items.filter((i) => i.type === tab)),
    [items, tab]
  );

  const firstName = me?.name?.split(" ")[0] ?? "there";

  return (
    <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 py-6 space-y-6">
      {/* Subtle hidden greeting — Inku's dashboard is content-first. */}
      <p className="sr-only">Welcome back, {firstName}.</p>

      {/* Filter row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Filter chips */}
        <div className="inline-flex items-center rounded-full bg-muted/50 p-1">
          {TAB_ORDER.map((t) => {
            const count = counts[t.key];
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  "px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5",
                  active
                    ? "bg-card shadow-card text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <span>{t.label}</span>
                <span
                  className={cn(
                    "text-xs",
                    active ? "text-muted-foreground" : "text-muted-foreground/60"
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Right: Owner / Sort / Create */}
        <div className="flex items-center gap-2">
          <FilterPill label="Owner" value="All" />
          <FilterPill label="Sort" value="Date created" />
          <Separator />
          <CreateButton />
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-16 text-center">
          <p className="text-sm text-muted-foreground italic">
            {tab === "all"
              ? "Nothing yet. Create your first canvas or briefing."
              : `No ${tab.toLowerCase()}s yet.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((item) => (
            <ItemCard key={item.id} item={item} onOpen={() => navigate(item.href)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── card ────────────────────────────────────────────────────────────
function ItemCard({ item, onOpen }: { item: Item; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="group text-left flex flex-col gap-2"
    >
      <div className="aspect-[4/3] w-full rounded-xl border border-border bg-card overflow-hidden shadow-card group-hover:shadow-card-hover transition-shadow relative">
        {/* Thumbnail */}
        {item.thumbGradient ? (
          <div className="w-full h-full" style={{ background: item.thumbGradient }} />
        ) : (
          <div className="w-full h-full bg-muted/30 flex items-center justify-center">
            <FileText className="h-12 w-12 text-muted-foreground/30" strokeWidth={1.25} />
          </div>
        )}
        {/* Type badge */}
        <TypeBadge type={item.type} />
      </div>
      <div className="px-1 space-y-0.5">
        <p className="text-sm font-medium line-clamp-1">{item.title}</p>
        <p className="text-xs text-muted-foreground">
          Modified {relTime(item.modifiedAt)}
        </p>
      </div>
    </button>
  );
}

function TypeBadge({ type }: { type: ItemType }) {
  const styles: Record<ItemType, string> = {
    Canvas: "bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200",
    Project: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200",
    Briefing: "bg-[hsl(var(--inku-blue))/.15] text-[hsl(var(--inku-blue))]",
  };
  const Icon = type === "Canvas" ? Layers : type === "Briefing" ? FileText : Sparkles;
  return (
    <span
      className={cn(
        "absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium",
        styles[type]
      )}
    >
      <Icon className="h-3 w-3" />
      {type}
    </span>
  );
}

// ─── small UI bits ───────────────────────────────────────────────────
function FilterPill({ label, value }: { label: string; value: string }) {
  return (
    <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
      <span className="font-medium">{label}:</span>
      <span>{value}</span>
      <ChevronDown className="h-3 w-3" />
    </button>
  );
}

function Separator() {
  return <div className="h-5 w-px bg-border" />;
}

function CreateButton() {
  const { createThread, createBriefing, setToast } = useStore();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Create
          <ChevronDown className="h-3 w-3 -mr-0.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] p-1.5">
        <CreateOption
          icon={Layers}
          tone="muted"
          name="Canvas"
          description="Visual workspace with AI chat"
          onSelect={() => createThread()}
        />
        <CreateOption
          icon={FileText}
          tone="inku"
          name="Briefing"
          description="Brief, moodboard, and linked canvases"
          onSelect={() =>
            setToast({
              kind: "ok",
              text: "Inku-style briefing workspace — port coming next.",
            })
          }
        />
        <CreateOption
          icon={Sparkles}
          tone="purple"
          name="Briefing V2"
          description="Agent-driven intake (talk to the Creative Director)"
          onSelect={() => createBriefing()}
        />
        <CreateOption
          icon={Folder}
          tone="amber"
          name="Folder"
          description="Organize canvases and briefings"
          onSelect={() =>
            setToast({ kind: "ok", text: "Folders are not wired up in this demo." })
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CreateOption({
  icon: Icon,
  tone,
  name,
  description,
  onSelect,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone: "muted" | "inku" | "purple" | "amber";
  name: string;
  description: string;
  onSelect: () => void;
}) {
  const toneStyles: Record<typeof tone, string> = {
    muted: "bg-muted text-muted-foreground",
    inku: "bg-[hsl(var(--inku-blue))/.12] text-[hsl(var(--inku-blue))]",
    purple:
      "bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300",
    amber:
      "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  };
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      className="cursor-pointer p-3 rounded-md flex items-start gap-3 group"
    >
      <div
        className={cn(
          "h-9 w-9 rounded-lg flex items-center justify-center shrink-0",
          toneStyles[tone]
        )}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight">{name}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
          {description}
        </p>
      </div>
    </DropdownMenuItem>
  );
}
